import { Component, ElementRef, ViewChild, AfterViewInit, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { AuthService } from 'src/app/Auth/AuthService';
import { SecureStorageService } from '@services/secure-storage.service';
import { WebQrSubscriptionService } from '@services/web-qr-subscription.service';
import { CommonModule } from '@angular/common';
import { MS_TOKEN_KEY } from 'src/app/constants/micro-api.service';

@Component({
  selector: 'app-payment-approved',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './payment-approved.component.html',
  styleUrls: ['../../../../auth.scss'],
})
export class PaymentApprovedComponent implements OnInit, AfterViewInit {

  @ViewChild('confetti') confettiContainer!: ElementRef;
  userId: string | null = null;
  sessionId: string | null = null;
  // Which purchase came back here. Web QR checkouts mark their return url with flow=web_qr and
  // confirm on their own action (2483); anything else is the campaign plan (2310).
  flow: string | null = null;
  authService = inject(AuthService);
  secureStorage = inject(SecureStorageService);
  webQrSubscriptionService = inject(WebQrSubscriptionService);
  isLoggedIn = false;
  isPaymentVerified = false;
  status: 'verifying' | 'paid' | 'pending' | 'failed' = 'verifying';
  failureMessage = '';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private pageService: PagesService,
    private sharedService: SharedService
  ) {
    this.updateLoginStatus();
  }

  ngOnInit(): void {
    const urlParams = new URLSearchParams(window.location.search);
    this.userId =
      urlParams.get('user_id') ||
      urlParams.get('userId') ||
      this.route.snapshot.paramMap.get('user_id');
    this.sessionId = urlParams.get('session_id') || urlParams.get('sessionId') || this.route.snapshot.queryParamMap.get('session_id');
    this.flow = urlParams.get('flow') || this.route.snapshot.queryParamMap.get('flow');
    this.updateLoginStatus();
  }

  private updateLoginStatus(): void {
    const hasToken = this.authService.isLoggedIn();
    const hasUserDetails = !!this.authService.getUserDetails();
    this.isLoggedIn = hasToken || hasUserDetails;
  }

  ngAfterViewInit(): void {
    this.checkPayment(this.userId, this.sessionId);
  }

  goToHomepage() {
    if (this.isLoggedIn) {
      this.router.navigate(['/home']);
    } else {
      this.router.navigate(['/auth/login']);
    }
    this.sharedService.hideLoader();
  }

  goToContests() {
    if (this.isLoggedIn) {
      this.router.navigate(['/home/campaigns']);
    } else {
      this.router.navigate(['/auth/login']);
    }
    this.sharedService.hideLoader();
  }

  goToInvoice() {
    if (!this.isLoggedIn) {
      this.router.navigate(['/auth/login']);
      this.sharedService.hideLoader();
      return;
    }

    this.router.navigate(['/home/billing'], {
      queryParams: {
        tab: 'invoice',
        payment_success: 'true'
      }
    });
    this.sharedService.hideLoader();
  }

  initializeConfetti() {
    setTimeout(() => {
      const container = this.confettiContainer?.nativeElement;

      if (!container) return;

      const totalPieces = 500;

      for (let i = 0; i < totalPieces; i++) {
        const confetti = document.createElement('div');
        confetti.classList.add('confetti-piece');

        confetti.style.left = Math.random() * 100 + 'vw';
        confetti.style.top = Math.random() * 10 + 'px';
        confetti.style.backgroundColor = `hsl(${Math.random() * 360}, 70%, 60%)`;

        const size = Math.random() * 8 + 4;
        confetti.style.width = `${size}px`;
        confetti.style.height = `${size}px`;

        confetti.style.animationDelay = Math.random() * 2 + 's';
        confetti.style.animationDuration = (Math.random() * 2 + 3) + 's';

        confetti.addEventListener('animationend', () => {
          confetti.remove();
        });

        container.appendChild(confetti);
      }
    }, 100);
  }

  // The page used to say "Payment Successful" whatever the answer — with no session id, on an
  // unpaid session, or on an error (shown as "[object Object]"). It now waits for 2310 and
  // shows what it said. 2310 knows the caller from the token and, without a session id, falls
  // back to the one stored on the pending subscription, so user_id is not sent.
  checkPayment(_user_id: string | null, session_id: string | null) {
    this.status = 'verifying';
    if (this.flow === 'web_qr') {
      this.confirmWebQrPayment(session_id);
      return;
    }
    this.sharedService.showLoader();

    this.pageService.update('CHECK_PAYMENT', session_id ? { session_id } : {}).subscribe({
      next: () => {
        this.sharedService.hideLoader();
        this.status = 'paid';
        this.isPaymentVerified = true;
        this.initializeConfetti();

        // Strip session_id from the URL once verified so a reload or browser
        // Back doesn't resend the same session_id and re-trigger this success screen.
        window.history.replaceState(null, '', window.location.pathname);

        localStorage.removeItem('sub_id');
        localStorage.removeItem('sub_plan_id');

        const userDetails = this.secureStorage.getItem<any>('userDetails');
        if (userDetails) {
          userDetails.premimum_user = true;
          void this.secureStorage.setItem('userDetails', userDetails);
        }

        this.signInAfterSignup();

        this.pageService.getAll('PURCHASED_PLAN').subscribe({
          next: (planResponse: any) => {
            const hasSdkAccess = planResponse?.sdk_features === true;
            const hasAiAccess = planResponse?.ai_attributes === true;
            const hasWebAccess = (planResponse?.sub_plan?.web_access ?? planResponse?.web_access) === true;
            void this.secureStorage.setItem('hasSdkAccess', hasSdkAccess);
            void this.secureStorage.setItem('hasAiAccess', hasAiAccess);
            void this.secureStorage.setItem('hasWebAccess', hasWebAccess);
            this.sharedService.updatedUserData.next(planResponse);
          },
          error: () => {
            this.sharedService.updatedUserData.next(true);
          }
        });
      },
      error: (err: any) => {
        this.sharedService.hideLoader();
        const code = err?.error?.code;
        this.status = code === 'NOT_PAID' ? 'pending' : 'failed';
        this.failureMessage = code === 'NOT_PAID'
          ? 'Your payment has not been completed yet. If you finished paying, refresh this page in a moment.'
          : err?.error?.message || 'We could not confirm your payment. If you were charged, contact support.';
      }
    });
  }

  // The Web QR plan is a separate subscription from the campaign plan, so on success nothing of
  // the campaign plan is touched here: no premimum_user flag, no PURCHASED_PLAN refresh.
  private confirmWebQrPayment(session_id: string | null) {
    if (!session_id) {
      this.status = 'failed';
      this.failureMessage = 'We could not confirm your payment. If you were charged, contact support.';
      return;
    }
    this.sharedService.showLoader();

    this.webQrSubscriptionService.confirmPayment(session_id).subscribe({
      next: () => {
        this.sharedService.hideLoader();
        this.status = 'paid';
        this.isPaymentVerified = true;
        this.initializeConfetti();
        // Strip session_id so a reload or Back does not re-confirm the same session.
        window.history.replaceState(null, '', window.location.pathname);
      },
      error: (err: any) => {
        this.sharedService.hideLoader();
        // 2483 answers FORBIDDEN while Stripe still reports the session unpaid.
        const pending = err?.error?.code === 'FORBIDDEN';
        this.status = pending ? 'pending' : 'failed';
        this.failureMessage = pending
          ? 'Your payment has not been completed yet. If you finished paying, refresh this page in a moment.'
          : err?.error?.message || 'We could not confirm your payment. If you were charged, contact support.';
      }
    });
  }

  retry() {
    this.checkPayment(this.userId, this.sessionId);
  }

  // A business that has just signed up arrives here holding only the onboarding token — the
  // dashboard session (authToken + userDetails) is only written by the login screen, so the
  // page used to send it to Login right after it paid. The token is already verified and
  // org-scoped, so the session is completed from 1027 (auth/me) and it goes straight on to the
  // dashboard. A business renewing from Billing is already signed in and keeps the buttons.
  private signInAfterSignup() {
    const msToken = localStorage.getItem(MS_TOKEN_KEY);
    if (this.authService.isAuthenticated() || !msToken) return;

    this.authService.getAll('GET_ME').subscribe({
      next: (me: any) => {
        // Same gate the dashboard guard applies — an unverified or company-less account stays out.
        if (!me?.verified || !me?.isCompany) return;

        this.authService.setToken({ user: { ...me, authToken: msToken, pass: true } });
        this.updateLoginStatus();

        const imgUrl = me.business_logo?.[0]?.url || me.isCompany?.business_logo?.[0]?.url || 'assets/images/profile.png';
        localStorage.setItem('profileImage', imgUrl);
        this.sharedService.updateProfileImage(imgUrl);

        // Long enough to see "Payment Successful", then on to the dashboard.
        setTimeout(() => this.router.navigate(['/home/quick-actions']), 1500);
      },
      // Not signed in after all — the Login button below still works.
      error: () => {},
    });
  }
}
